import { skipToken } from '@reduxjs/toolkit/query';
import {
  ColumnDef,
  getCoreRowModel,
  PaginationState,
  SortingState,
  useReactTable
} from '@tanstack/react-table';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useBuildingServiceListAreasForBuildingQuery } from '../../../../api/enhanced/enhancedBuildings';
import { V1Alpha1Checkpoint } from '../../../../api/gen/checkpoints';
import {
  enhancedCheckpointApi,
  useCheckpointServiceListCheckpointsForBuildingQuery
} from '../../../../api/enhanced/enhancedCheckpoints';
import DataTable from '../../../../components/tables/dataTable';
import RowCell from '../../../../components/tables/rowCell';
import TableDetailButtons from '../../../../components/tables/detailButtons';
import BatteryPill from '../../../../components/pill/batteryPill';

interface TrapTableProps {
  groupId?: string;
  buildingId?: string;
  searchTerm?: string;
  editDeviceModalState: (isOpen: boolean, trapId: string) => void;
  deleteDeviceModalState: (
    isOpen: boolean,
    checkpointId: string,
    sensorId: string
  ) => void;
}

function TrapTable({
  groupId,
  buildingId,
  searchTerm,
  editDeviceModalState,
  deleteDeviceModalState
}: TrapTableProps): ReactElement {
  const [sortColumns, setSortColumns] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);

  useEffect(() => {
    let sortingColumns = 'checkpoint_number:ASC';
    if (sorting.length > 0) {
      sorting.forEach((sortCol) => {
        sortingColumns = `${sortCol.id}:${sortCol.desc ? 'DESC' : 'ASC'}`;
      });
    }
    setSortColumns(sortingColumns);
  }, [sorting, setSortColumns]);

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20
  });

  useEffect(() => {
    enhancedCheckpointApi.util.invalidateTags(['SENSOR_TYPE_TRAP']);
    setPagination((state) => ({ ...state, pageIndex: 0 }));
    setSortColumns('checkpoint_number:ASC');
  }, [pageSize, searchTerm]);

  const {
    data: areaData,
    isLoading: areaIsLoading,
    isSuccess: areaIsSuccess,
    isError: areaIsError,
    error: areaError
  } = useBuildingServiceListAreasForBuildingQuery(
    buildingId != null && groupId != null
      ? { buildingId, groupId, limit: 200, page: 0 }
      : skipToken
  );

  const columns = useMemo<ColumnDef<V1Alpha1Checkpoint>[]>(() => {
    const getAreaDescriptionById = (id: string): string => {
      if (areaIsError) {
        return 'Fehler beim Laden der Bereiche';
      }

      const area = areaData?.areas?.find(
        (currentArea) => currentArea.id === id
      );
      return area != null && area.description != null
        ? area.description
        : 'Keinem Bereich zugewiesen';
    };

    return [
      {
        header: 'Prüfpunkt',
        id: 'checkpoint_number',
        cell: (info) => RowCell({ title: info.getValue<string>() }),
        accessorFn: (row) => row.checkpointNumber,
        footer: (props) => props.column.id
      },
      {
        header: 'Beschreibung',
        id: 'description',
        accessorFn: (row) => row.description,
        cell: (info) => RowCell({ title: info.getValue<string>() }),
        footer: (props) => props.column.id
      },
      {
        header: 'Bereich',
        accessorFn: (row) => row.areaId,
        cell: (info) =>
          RowCell({
            title: getAreaDescriptionById(info.getValue<string>())
          }),
        footer: (props) => props.column.id,
        enableSorting: false
      },
      {
        header: 'Seriennummer',
        id: 'current_sensor_id',
        accessorFn: (row) => row.currentSensorId,
        cell: (info) => RowCell({ title: info.getValue<string>() }),
        footer: (props) => props.column.id
      },
      {
        header: 'Batteriezustand',
        id: 'battery_voltage',
        cell: (info) => BatteryPill({ value: info.getValue<number>() }),
        accessorFn: (row) => row.trapPayload?.batteryVoltage,
        footer: (props) => props.column.id
      },
      {
        header: 'Details',
        accessorFn: (row) => row.id,
        cell: (info) =>
          TableDetailButtons({
            editHandler: () => {
              editDeviceModalState(true, info.getValue<string>());
            },
            deleteHandler: () => {
              deleteDeviceModalState(
                true,
                info.getValue<string>(),
                info.row.original.currentSensorId!
              );
            }
          }),
        footer: (props) => props.column.id,
        enableSorting: false
      }
    ];
  }, [
    areaData?.areas,
    areaIsError,
    deleteDeviceModalState,
    editDeviceModalState
  ]);

  const { data, isLoading, isSuccess, isError, error } =
    useCheckpointServiceListCheckpointsForBuildingQuery(
      buildingId != null && groupId != null
        ? {
            groupId,
            buildingId,
            limit: pageSize,
            page: pageIndex,
            sensorType: 'SENSOR_TYPE_TRAP',
            sortBy: sortColumns,
            searchBy: searchTerm != null ? searchTerm : ''
          }
        : skipToken
    );

  const pagination = useMemo(
    () => ({ pageIndex, pageSize }),
    [pageSize, pageIndex]
  );

  const total = Number(data?.total);
  const limit = data?.limit;
  const pageCount =
    total != null && limit != null ? Math.ceil(total / limit) : -1;

  const table = useReactTable({
    data: data?.checkpoints ?? [],
    columns,
    pageCount,
    getCoreRowModel: getCoreRowModel(),
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    manualPagination: true,
    manualSorting: true
  });

  return (
    <DataTable
      isLoading={isLoading && areaIsLoading}
      isError={isError}
      isEmpty={
        isSuccess &&
        (data.checkpoints == null || data.checkpoints.length === 0) &&
        areaIsSuccess
      }
      pageSize={pageSize}
      table={table}
    />
  );
}

export default TrapTable;
